import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getUser } from '@core/actions/UserActions';

import { fetchBalance } from '../../account/actions/balanceActions';

// Pulls the bits the Header needs: auth state (+ user) and balance.
// - Auth state comes from `@core/reducers/UserReducer` (mounted as `state.user`
//   by every bidding bundle via the shared `configureStore`). Bundles don't
//   pre-dispatch `getUser()`, so we resolve auth here on first mount — without
//   that the `{authorized && …}` gate in Header.jsx never flips and the
//   account dropdown never renders.
// - Balance comes from `state.balance` — the account bundle wires it directly,
//   other bundles (bidding-flow) combine `balanceReducer` in their store too.
//   This hook falls back to a safe empty shape if the slice is missing.
export default function useHeaderData({ fetchBalanceOnMount = true } = {}) {
    const dispatch = useDispatch();

    const authStatus = useSelector(state => state.user?.authorized);
    const authorized = authStatus === true;
    const userRecord = useSelector(state => state.user?.user || null);
    const balance = useSelector(state => state.balance || null);

    useEffect(() => {
        // Fire when auth is unknown, OR when pre-seeded as authorized but
        // user details (name, feature_flags) haven't loaded yet.
        if (authStatus === undefined || (authStatus === true && !userRecord?.id)) {
            dispatch(getUser());
        }
    }, [dispatch, authStatus, userRecord?.id]);

    useEffect(() => {
        if (fetchBalanceOnMount && authorized) {
            dispatch(fetchBalance());
        }
    }, [dispatch, fetchBalanceOnMount, authorized]);

    return {
        authorized,
        userName: userRecord?.name || null,
        balanceAmount: balance?.amount_value ?? '0.00',
        referralEnabled: Boolean(userRecord?.feature_flags?.referral_enabled),
    };
}
